//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ForwardedRef, forwardRef } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import TextAreaInput from '@components/atoms/text-area-input/TextAreaInput';
import { FormFieldControllerProps } from '@components/form/field-controller/FormFieldController';

interface FormTextInputProps<
  T extends FieldValues,
  TFieldName extends FieldPath<T>,
> extends Omit<
      React.ComponentPropsWithoutRef<typeof TextAreaInput>,
      'onChange' | 'errorText' | 'hasError' | 'name' | 'onBlur' | 'value'
    >,
    FormFieldControllerProps<T, TFieldName> {}

function FormTextAreaInputInner<
  T extends FieldValues,
  TFieldName extends FieldPath<T>,
>(
  props: FormTextInputProps<T, TFieldName>,
  ref: ForwardedRef<HTMLTextAreaElement>,
) {
  const { error, invalid, onChange, ...rest } = props;
  return (
    <TextAreaInput
      {...rest}
      ref={ref}
      errorText={error?.message}
      hasError={invalid}
      onChange={onChange}
    />
  );
}

/**
 * Generic forwardRef workaround for React 18 + TypeScript.
 *
 * TypeScript cannot infer generic type parameters through forwardRef's higher-order
 * function signature. The `as any` cast is only used internally for wiring - consumers
 * of this component still get full generic type inference at the call site.
 *
 * This is the standard community workaround documented in:
 * - https://github.com/microsoft/TypeScript/issues/30215
 *
 * React 19 will support `ref` as a regular prop, eliminating the need for forwardRef.
 * See: https://github.com/reactjs/rfcs/pull/107
 */
export const FormTextAreaInput = forwardRef(FormTextAreaInputInner as any) as {
  <T extends FieldValues, TFieldName extends FieldPath<T>>(
    props: FormTextInputProps<T, TFieldName> & {
      ref?: ForwardedRef<HTMLTextAreaElement>;
    },
  ): React.ReactElement;
  displayName?: string;
};

FormTextAreaInput.displayName = 'FormTextAreaInput';
